import React, { Component, createRef, RefObject } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4maps from '@amcharts/amcharts4/maps';
import am4geodataWorldLow from '@amcharts/amcharts4-geodata/worldLow';

import { defaultHexColors } from '../colors';

/**
 * @typedef {EarningsPerCountryProps}
 * @property {string} chartDivId a unique ID for this chart instance
 * @property {number} chartHeight height for the chart div
 * @property {ProCountryCode[]} countryCodes array of ProCountryCode elements to render
 * @property {number} legendHeight height for the legend div
 */
interface EarningsPerCountryProps {
    chartDivId: string;
    chartHeight: number;
    countryCodes: ProCountryCode[];
    legendHeight: number;
}

interface CountryCodeMapItem {
    id: Maybe<string>;
    value: Maybe<number>;
}

const countryCodeMapper = ({
    country_code,
    earnings,
}: ProCountryCode): CountryCodeMapItem => ({
    id: country_code ?? null,
    value: earnings === undefined ? null : earnings,
});

export class EarningsPerCountry extends Component<EarningsPerCountryProps> {
    private chart: am4maps.MapChart | null = null;

    private polygonSeries: am4maps.MapPolygonSeries | null = null;

    // There is a race condition in amCharts which can cause issues if we use a chart div ID rather
    // than a ref. The chartDivId prop is only used as a fallback for type safety; the ref
    // should be available by the time componentDidMount is called.
    private divRef: RefObject<HTMLDivElement> | null = null;

    private legendDivId: string;

    constructor(props: EarningsPerCountryProps) {
        super(props);

        this.divRef = createRef();
        this.legendDivId = `${props.chartDivId}.legend`;
    }

    componentDidMount(): void {
        am4core.createDeferred((): am4core.Sprite => {
            const { chartDivId, countryCodes } = this.props;

            const chart = am4core.create(
                this.divRef?.current ?? chartDivId,
                am4maps.MapChart,
            );

            chart.numberFormatter.numberFormat = '$#,###.00';
            chart.hiddenState.properties.opacity = 0; // this creates initial fade-in
            chart.geodata = am4geodataWorldLow;
            chart.projection = new am4maps.projections.Miller();

            const polygonSeries = chart.series.push(
                new am4maps.MapPolygonSeries(),
            );
            const polygonTemplate = polygonSeries.mapPolygons.template;

            polygonSeries.useGeodata = true;
            polygonTemplate.tooltipText = '{name} {value}';
            polygonSeries.mapPolygons.template.fill = am4core.color(
                defaultHexColors[0],
            );
            polygonSeries.heatRules.push({
                property: 'fill',
                target: polygonSeries.mapPolygons.template,
                min: am4core.color(defaultHexColors[2]),
                max: am4core.color(defaultHexColors[8]),
            });

            // add heat legend
            const heatLegendContainer = am4core.create(
                this.legendDivId,
                am4core.Container,
            );

            heatLegendContainer.width = am4core.percent(100);
            heatLegendContainer.height = am4core.percent(100);

            const heatLegend = heatLegendContainer.createChild(
                am4maps.HeatLegend,
            );

            heatLegend.numberFormatter.numberFormat = '$#,###.00';
            heatLegend.valign = 'bottom';
            heatLegend.align = 'left';
            heatLegend.padding(20, 20, 20, 20);
            heatLegend.width = am4core.percent(100);
            heatLegend.series = polygonSeries;
            heatLegend.orientation = 'horizontal';
            heatLegend.valueAxis.renderer.labels.template.fontSize = '.8rem';
            heatLegend.valueAxis.renderer.minGridDistance = 50;

            function handleHover(mapPolygon: am4maps.MapPolygon): void {
                // eslint-disable-next-line no-restricted-globals
                if (!isNaN(mapPolygon.dataItem.value)) {
                    heatLegend.valueAxis.showTooltipAt(
                        mapPolygon.dataItem.value,
                    );
                } else {
                    heatLegend.valueAxis.hideTooltip();
                }
            }

            polygonSeries.mapPolygons.template.events.on('over', event => {
                handleHover(event.target);
            });

            polygonSeries.mapPolygons.template.events.on('hit', event => {
                handleHover(event.target);
            });

            polygonSeries.mapPolygons.template.strokeOpacity = 0.4;
            polygonSeries.mapPolygons.template.events.on('out', () => {
                heatLegend.valueAxis.hideTooltip();
            });

            chart.zoomControl = new am4maps.ZoomControl();
            chart.zoomControl.valign = 'top';

            polygonSeries.data = countryCodes.map(countryCodeMapper);

            // excludes Antarctica
            polygonSeries.exclude = ['AQ'];

            this.chart = chart;
            this.polygonSeries = polygonSeries;

            return chart;
        }, this);
    }

    componentDidUpdate(prevProps: EarningsPerCountryProps): void {
        const { countryCodes } = this.props;

        if (
            prevProps.countryCodes !== countryCodes &&
            this.polygonSeries !== null
        ) {
            this.polygonSeries.data = countryCodes.map(countryCodeMapper);
        }
    }

    componentWillUnmount(): void {
        if (this.chart) {
            this.chart.dispose();
        }
    }

    render(): JSX.Element {
        const { chartDivId, chartHeight, legendHeight } = this.props;

        return (
            <>
                <div
                    id={chartDivId}
                    ref={this.divRef}
                    data-cy="earnings-per-country-chart"
                    style={{ width: '100%', height: chartHeight }}
                />
                <div
                    id={this.legendDivId}
                    style={{ width: '100%', height: legendHeight }}
                />
            </>
        );
    }
}
